import React from 'react';
import {
  Breadcrumb, Card, Row, Col, Form, Input, Button, message, Modal,
  Table, Tag, Tabs, Badge, Spin, Empty, Typography, Tooltip,
  Space, Divider, DatePicker, Select,
} from 'antd';
import {
  CarOutlined, ExclamationCircleOutlined, CheckCircleOutlined,
  CloseCircleOutlined, SendOutlined, ClockCircleOutlined,
  SearchOutlined, ReloadOutlined, FileTextOutlined, AuditOutlined,
  SafetyCertificateOutlined, WarningOutlined, ScissorOutlined,
  CrownOutlined, TeamOutlined, UserOutlined, FileAddOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { connect } from 'react-redux';
import { initHandler, ajaxViewHandler, ajaxHandler, setConfig } from '../../store/actions';
import { Helmet } from 'react-helmet';
import Config from '../../Config';
import moment from 'moment';
import { toastSuccess, toastError } from '../../utils/toast';

const { Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const configClass = {
  title: 'Batal Notice',
  apiUrl: {
    tagihan:    'transaksi/api/list-tagihan/',
    permintaan: 'transaksi/api/permintaan-batal-notice/',
    input_manual: 'transaksi/api/permintaan-batal-notice/input-manual/',
    notice_rusak: 'transaksi/api/notice-rusak/',
    upt:        'pegawai/api/upt/',
  },
  breadCrumb: [<span><CarOutlined /> Transaksi</span>, 'Batal Notice'],
};

const STATUS_COLOR = { pending: 'orange', disetujui: 'green', ditolak: 'red' };
const STATUS_ICON  = { pending: <ClockCircleOutlined />, disetujui: <CheckCircleOutlined />, ditolak: <CloseCircleOutlined /> };
const STATUS_LABEL = { pending: 'Menunggu', disetujui: 'Disetujui', ditolak: 'Ditolak' };

// 5 kategori baku pembatalan - menggantikan pilihan jenis manual + keterangan
// bebas-teks. jenis (rusak/batal_validasi) diturunkan otomatis dari kategori.
const KATEGORI_OPTIONS = [
  { value: 'batal_pembayaran',  label: 'Batal Pembayaran', jenis: 'batal_validasi' },
  { value: 'salah_input_nopol', label: 'Salah Input Nomor Polisi', jenis: 'rusak' },
  { value: 'rusak_printer',     label: 'Notice Rusak Karena Printer Error', jenis: 'rusak' },
  { value: 'salah_penetapan',   label: 'Salah Penetapan PKB dan SWDKLLJ', jenis: 'rusak' },
  { value: 'tercetak_ganda',    label: 'Notice Tercetak Ganda', jenis: 'rusak' },
];
const KATEGORI_JENIS_MAP = KATEGORI_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.jenis }), {});

/** Format nomor notice → "C 6624" atau hanya "6624" jika tidak ada kode */
const fmtNotice = (kode, nomor) => {
  if (!nomor) return '-';
  return kode ? `${kode} ${nomor}` : String(nomor);
};

const config = new Config();

class CancelNotice extends React.Component {
  formRef       = React.createRef();
  formBatalRef  = React.createRef();
  formManualRef = React.createRef();

  constructor(props) {
    super(props);
    this.state = {
      // Tagihan list
      loadingTagihan: false, listTagihan: [], searchNopol: '',
      pagination: { current: 1, pageSize: 10, total: 0 },

      // Permintaan (kasir riwayat + kasi pending)
      loadingPermintaan: false, listPermintaan: [],
      tabAktif: null, // set on mount based on user type
      permintaanFilterStatus: 'pending', // kartu statistik Menunggu/Disetujui/Ditolak yg aktif di tab Permintaan Masuk

      // Log (superadmin/admin)
      loadingLog: false, listLog: [],
      logFilter: { tanggal_dari: '', tanggal_sampai: '', upt_id: '' },
      logPagination: { current: 1, pageSize: 15, total: 0 },

      // UPT list (superadmin only)
      loadingUpt: false, listUpt: [],

      // Modals
      modalAjukan: false, loadingAjukan: false, selectedTagihan: null,
      modalBatalkan: false, loadingBatalkan: false,
      modalApprove: false, modalReject: false,
      selectedPerm: null, loadingAction: false, catatanKasi: '',

      // Input manual (blanko rusak tanpa transaksi)
      manualInfo: null, loadingManualInfo: false,
      manualPreview: null, loadingManualPreview: false,
      modalManual: false, loadingManualSubmit: false,
      manualValues: null,

      isMobile: window.innerWidth <= 768,
    };
  }

  componentDidMount() {
    this.props.initHandler();
    window.addEventListener('resize', () => this.setState({ isMobile: window.innerWidth <= 768 }));
    const tab = this.canDirectCancel() ? (this.isSuperadmin() || this.isAdmin() ? 'batalkan' : 'batalkan') : 'ajukan';
    this.setState({ tabAktif: tab });
    this.fetchTagihan();
    this.fetchPermintaan();
    if (this.isSuperadmin()) this.fetchUptList();
    if (this.canInputManual()) this.fetchManualInfo();
  }

  // ── User type helpers ────────────────────────────────────────
  isSuperadmin  = () => this.props.level_user === 1;
  isAdmin       = () => this.props.level_user === 2;
  isApprover    = () => !!(this.props.akses && this.props.akses.transaksi_cancle_notice_approve);
  isKasir       = () => !!(this.props.akses && this.props.akses.transaksi_cancle_notice)
                          && !this.canDirectCancel();
  canDirectCancel = () => this.isSuperadmin() || this.isAdmin() || this.isApprover();
  canSeeAll     = () => this.isSuperadmin();
  // Input batal manual: level 1/2 otomatis, selain itu wajib key hak_akses
  // sendiri (transaksi_batal_notice_manual) - terpisah dari batal notice biasa.
  canInputManual = () => this.isSuperadmin() || this.isAdmin()
    || !!(this.props.akses && this.props.akses.transaksi_batal_notice_manual);

  // ── Fetch helpers ────────────────────────────────────────────
  fetchTagihan = (search = '', page = 1) => {
    const { pagination } = this.state;
    const limit = pagination.pageSize, offset = (page - 1) * limit;
    const params = `?for_cancel_notice=true&limit=${limit}&offset=${offset}${search ? '&search=' + encodeURIComponent(search) : ''}`;
    this.setState({ loadingTagihan: true });
    this.props.ajaxViewHandler('get', configClass.apiUrl.tagihan + params)
      .then(() => {
        if (this.props.isSuccess) {
          const data = this.props.responseMsg;
          this.setState({
            listTagihan: Array.isArray(data) ? data : (data.results || []),
            pagination: { ...pagination, current: page, total: data.count || 0 },
          });
        }
        this.setState({ loadingTagihan: false });
      }).catch(() => this.setState({ loadingTagihan: false }));
  };

  fetchPermintaan = () => {
    this.setState({ loadingPermintaan: true });
    this.props.ajaxViewHandler('get', configClass.apiUrl.permintaan)
      .then(() => {
        if (this.props.isSuccess) {
          const data = this.props.responseMsg;
          this.setState({ listPermintaan: Array.isArray(data) ? data : (data.results || []) });
        }
        this.setState({ loadingPermintaan: false });
      }).catch(() => this.setState({ loadingPermintaan: false }));
  };

  fetchLog = (page = 1) => {
    const { logFilter, logPagination } = this.state;
    const limit = logPagination.pageSize, offset = (page - 1) * limit;
    let params = `?limit=${limit}&offset=${offset}`;
    if (logFilter.tanggal_dari)   params += `&tanggal_dari=${logFilter.tanggal_dari}`;
    if (logFilter.tanggal_sampai) params += `&tanggal_sampai=${logFilter.tanggal_sampai}`;
    if (logFilter.upt_id)         params += `&upt_id=${logFilter.upt_id}`;
    this.setState({ loadingLog: true });
    this.props.ajaxViewHandler('get', configClass.apiUrl.permintaan + params)
      .then(() => {
        if (this.props.isSuccess) {
          const data = this.props.responseMsg;
          this.setState({
            listLog: Array.isArray(data) ? data : (data.results || []),
            logPagination: { ...logPagination, current: page, total: data.count || 0 },
          });
        }
        this.setState({ loadingLog: false });
      }).catch(() => this.setState({ loadingLog: false }));
  };

  fetchUptList = () => {
    this.setState({ loadingUpt: true });
    this.props.ajaxViewHandler('get', configClass.apiUrl.upt + '?limit=100')
      .then(() => {
        if (this.props.isSuccess) {
          const data = this.props.responseMsg;
          this.setState({ listUpt: Array.isArray(data) ? data : (data.results || []) });
        }
        this.setState({ loadingUpt: false });
      }).catch(() => this.setState({ loadingUpt: false }));
  };

  // ── Actions ──────────────────────────────────────────────────
  // Peringatan konfirmasi sebelum benar-benar submit - menampilkan nomor
  // polisi, kode notice, dan kategori yang dipilih supaya petugas tidak
  // salah pilih kategori tanpa sadar (lihat diskusi 2026-10-09).
  confirmAjukan = (values) => {
    const { selectedTagihan } = this.state;
    const opt = KATEGORI_OPTIONS.find(o => o.value === values.kategori);
    Modal.confirm({
      title: 'Yakin ajukan pembatalan notice ini?',
      icon: <ExclamationCircleOutlined style={{ color: '#faad14' }} />,
      width: 440,
      content: (
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Nomor Polisi</Text>
            <div><Text strong style={{ fontSize: 15 }}>{selectedTagihan.nomor_polisi}</Text></div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Kode Notice</Text>
            <div><Text strong>{fmtNotice(selectedTagihan.kode_notice, selectedTagihan.notice_number)}</Text></div>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12 }}>Kategori Pembatalan</Text>
            <div>
              {opt && opt.jenis === 'rusak'
                ? <span><WarningOutlined style={{ color: '#fa8c16', marginRight: 6 }} /><Text strong>{opt.label}</Text> <Text type="secondary" style={{ fontSize: 12 }}>— Notice Rusak</Text></span>
                : <span><CloseCircleOutlined style={{ color: '#1890ff', marginRight: 6 }} /><Text strong>{opt ? opt.label : values.kategori}</Text> <Text type="secondary" style={{ fontSize: 12 }}>— Notice Tidak Rusak</Text></span>}
            </div>
          </div>
        </div>
      ),
      okText: 'Ya, Ajukan', cancelText: 'Batal',
      okButtonProps: { style: { borderRadius: 8, background: '#0f2847', borderColor: '#0f2847' } },
      cancelButtonProps: { style: { borderRadius: 8 } },
      onOk: () => this.handleAjukan(values),
    });
  };

  handleAjukan = (values) => {
    const { selectedTagihan } = this.state;
    this.setState({ loadingAjukan: true });
    const payload = {
      tagihan: selectedTagihan.id, nomor_polisi: selectedTagihan.nomor_polisi,
      nomor_notice: selectedTagihan.notice_number, kategori: values.kategori,
    };
    this.props.ajaxHandler('post', configClass.apiUrl.permintaan, payload, false, true)
      .then(() => {
        if (this.props.isSuccess) {
          message.success({ content: 'Permintaan berhasil dikirim. Menunggu persetujuan Kasi Penetapan.', duration: 2, key: 'toast' });
          this.setState({ modalAjukan: false, tabAktif: 'riwayat' });
          this.fetchPermintaan();
          if (this.formRef.current) this.formRef.current.resetFields();
        } else {
          message.error({ content: typeof this.props.responseMsg === 'string' ? this.props.responseMsg : 'Gagal mengirim.', duration: 2, key: 'toast' });
        }
        this.setState({ loadingAjukan: false });
      }).catch(() => { message.error({ content: 'Gagal.', duration: 2, key: 'toast' }); this.setState({ loadingAjukan: false }); });
  };

  // Sama seperti confirmAjukan - tapi tindakannya LANGSUNG eksekusi (tidak
  // lewat persetujuan Kasi Penetapan), jadi peringatan di sini lebih tegas.
  confirmBatalkanLangsung = (values) => {
    const { selectedTagihan } = this.state;
    const opt = KATEGORI_OPTIONS.find(o => o.value === values.kategori);
    Modal.confirm({
      title: 'Yakin batalkan notice ini langsung?',
      icon: <ExclamationCircleOutlined style={{ color: '#cf1322' }} />,
      width: 440,
      content: (
        <div style={{ marginTop: 8 }}>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Nomor Polisi</Text>
            <div><Text strong style={{ fontSize: 15, color: '#cf1322' }}>{selectedTagihan.nomor_polisi}</Text></div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Kode Notice</Text>
            <div><Text strong>{fmtNotice(selectedTagihan.kode_notice, selectedTagihan.notice_number)}</Text></div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>Kategori Pembatalan</Text>
            <div>
              {opt && opt.jenis === 'rusak'
                ? <span><WarningOutlined style={{ color: '#fa8c16', marginRight: 6 }} /><Text strong>{opt.label}</Text> <Text type="secondary" style={{ fontSize: 12 }}>— Notice Rusak</Text></span>
                : <span><CloseCircleOutlined style={{ color: '#1890ff', marginRight: 6 }} /><Text strong>{opt ? opt.label : values.kategori}</Text> <Text type="secondary" style={{ fontSize: 12 }}>— Notice Tidak Rusak</Text></span>}
            </div>
          </div>
          <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: '8px 12px' }}>
            <Text style={{ fontSize: 12, color: '#a8071a' }}>
              Tindakan ini langsung diterapkan (tanpa persetujuan) dan tercatat untuk audit.
            </Text>
          </div>
        </div>
      ),
      okText: 'Ya, Batalkan', cancelText: 'Batal',
      okButtonProps: { danger: true, style: { borderRadius: 8 } },
      cancelButtonProps: { style: { borderRadius: 8 } },
      onOk: () => this.handleBatalkanLangsung(values),
    });
  };

  handleBatalkanLangsung = (values) => {
    const { selectedTagihan } = this.state;
    const jenisDerived = KATEGORI_JENIS_MAP[values.kategori];
    const endpoint = jenisDerived === 'rusak' ? 'delete_notice' : 'batal_validasi';
    const datas = new FormData();
    datas.append('id_transaksi', selectedTagihan.id);
    datas.append('nomor_polisi', selectedTagihan.nomor_polisi);
    datas.append('kategori', values.kategori);
    datas.append('nomor_notice', selectedTagihan.notice_number || 0);
    datas.append('tanggal_penetapan', selectedTagihan.tgl_penetapan || '');
    datas.append('status_notice', jenisDerived === 'rusak' ? 2 : 1);
    this.setState({ loadingBatalkan: true });
    this.props.ajaxHandler('post',
      `${configClass.apiUrl.notice_rusak}${selectedTagihan.nomor_polisi}/${endpoint}/`,
      datas, true, true)
      .then(() => {
        if (this.props.isSuccess) {
          message.success({ content: 'Notice berhasil dibatalkan. Log tersimpan untuk audit.', duration: 2, key: 'toast' });
          this.setState({ modalBatalkan: false });
          this.fetchTagihan(this.state.searchNopol);
          this.fetchPermintaan();
          if (this.formBatalRef.current) this.formBatalRef.current.resetFields();
        } else {
          message.error({ content: typeof this.props.responseMsg === 'string' ? this.props.responseMsg : 'Gagal.', duration: 2, key: 'toast' });
        }
        this.setState({ loadingBatalkan: false });
      }).catch(() => { message.error({ content: 'Gagal.', duration: 2, key: 'toast' }); this.setState({ loadingBatalkan: false }); });
  };

  handleApprove = () => {
    const { selectedPerm, catatanKasi } = this.state;
    this.setState({ loadingAction: true });
    this.props.ajaxHandler('post',
      `${configClass.apiUrl.permintaan}${selectedPerm.id}/approve/`,
      { catatan_kasi: catatanKasi }, false, true)
      .then(() => {
        if (this.props.isSuccess) {
          message.success({ content: 'Notice berhasil dibatalkan.', duration: 2, key: 'toast' });
          this.setState({ modalApprove: false, catatanKasi: '' });
          this.fetchPermintaan();
          this.props.setConfig({ cancelNoticePendingCount: Math.max(0, (this.props.cancelNoticePendingCount || 1) - 1) });
        } else {
          message.error({ content: 'Gagal menyetujui.', duration: 2, key: 'toast' });
        }
        this.setState({ loadingAction: false });
      }).catch(() => { message.error({ content: 'Gagal.', duration: 2, key: 'toast' }); this.setState({ loadingAction: false }); });
  };

  handleReject = () => {
    const { selectedPerm, catatanKasi } = this.state;
    if (!catatanKasi.trim()) { message.warning({ content: 'Alasan penolakan wajib diisi.', duration: 2, key: 'toast' }); return; }
    this.setState({ loadingAction: true });
    this.props.ajaxHandler('post',
      `${configClass.apiUrl.permintaan}${selectedPerm.id}/reject/`,
      { catatan_kasi: catatanKasi }, false, true)
      .then(() => {
        if (this.props.isSuccess) {
          message.success({ content: 'Permintaan ditolak.', duration: 2, key: 'toast' });
          this.setState({ modalReject: false, catatanKasi: '' });
          this.fetchPermintaan();
          this.props.setConfig({ cancelNoticePendingCount: Math.max(0, (this.props.cancelNoticePendingCount || 1) - 1) });
        } else {
          message.error({ content: 'Gagal menolak.', duration: 2, key: 'toast' });
        }
        this.setState({ loadingAction: false });
      }).catch(() => { message.error({ content: 'Gagal.', duration: 2, key: 'toast' }); this.setState({ loadingAction: false }); });
  };

  // ── Input manual (blanko rusak tanpa transaksi) ──────────────
  _extractManualError = (resp, fallback) => {
    if (resp && typeof resp === 'object' && resp.error) return resp.error;
    if (typeof resp === 'string' && resp) return resp;
    return fallback;
  };

  fetchManualInfo = () => {
    this.setState({ loadingManualInfo: true });
    this.props.ajaxViewHandler('get', configClass.apiUrl.input_manual)
      .then(() => {
        if (this.props.isSuccess) {
          const info = this.props.responseMsg;
          this.setState({ manualInfo: info });
          if (this.formManualRef.current) {
            this.formManualRef.current.setFieldsValue({ kode_notice: info.kode || '' });
          }
        }
        this.setState({ loadingManualInfo: false });
      }).catch(() => this.setState({ loadingManualInfo: false }));
  };

  handleManualPeriksa = (values) => {
    const payload = {
      kode_notice: values.kode_notice,
      nomor_dari: parseInt(values.nomor_dari, 10),
      nomor_sampai: parseInt(values.nomor_sampai || values.nomor_dari, 10),
      kategori: values.kategori || 'rusak_printer',
      keterangan: values.keterangan || '',
      nomor_polisi: values.nomor_polisi || '',
      // Wajib diisi backend kalau nomor_polisi diisi - dipakai mencocokkan
      // ke Tagihan asli supaya baris ini nyambung di Laporan Penggunaan
      // Notice (lihat diskusi 2026-10-06).
      tanggal_penetapan: values.tanggal_penetapan ? values.tanggal_penetapan.format('DD-MM-YYYY') : '',
      preview: true,
    };
    this.setState({ loadingManualPreview: true });
    this.props.ajaxHandler('post', configClass.apiUrl.input_manual, payload, false, true)
      .then(() => {
        if (this.props.isSuccess) {
          this.setState({
            manualPreview: this.props.responseMsg,
            manualValues: payload,
            modalManual: true,
          });
        } else {
          toastError(this._extractManualError(this.props.responseMsg, 'Gagal memeriksa nomor'), 5);
        }
        this.setState({ loadingManualPreview: false });
      }).catch(() => {
        toastError('Gagal memeriksa nomor, coba lagi');
        this.setState({ loadingManualPreview: false });
      });
  };

  handleManualKonfirmasi = () => {
    const { manualValues } = this.state;
    const payload = { ...manualValues };
    delete payload.preview;
    this.setState({ loadingManualSubmit: true });
    this.props.ajaxHandler('post', configClass.apiUrl.input_manual, payload, false, true)
      .then(() => {
        if (this.props.isSuccess) {
          const data = this.props.responseMsg || {};
          toastSuccess(`${data.jumlah || ''} nomor dicatat rusak dan otomatis dilompati saat pembayaran`, 5);
          this.setState({ modalManual: false, manualPreview: null, manualValues: null });
          if (this.formManualRef.current) {
            this.formManualRef.current.resetFields(['nomor_dari', 'nomor_sampai', 'keterangan', 'nomor_polisi', 'tanggal_penetapan']);
          }
          this.fetchPermintaan();
          this.fetchManualInfo();
        } else {
          toastError(this._extractManualError(this.props.responseMsg, 'Gagal menyimpan'), 5);
        }
        this.setState({ loadingManualSubmit: false });
      }).catch(() => {
        toastError('Gagal menyimpan, coba lagi');
        this.setState({ loadingManualSubmit: false });
      });
  };

  renderManualTab = () => {
    const { manualInfo, loadingManualPreview } = this.state;
    const kategoriRusak = KATEGORI_OPTIONS.filter(o => o.jenis === 'rusak');
    return (
      <div>
        <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8,
                      padding: '8px 14px', marginBottom: 14, fontSize: 12, color: '#614700' }}>
          <PrinterOutlined style={{ marginRight: 8, color: '#faad14' }} />
          Untuk blanko fisik yang rusak <b>tanpa pernah dipakai transaksi</b> (sobek di tumpukan,
          gap lama yang belum ter-akun). Nomor langsung tercatat rusak tanpa approval dan
          otomatis dilompati saat pembayaran. Untuk notice yang sudah dipakai transaksi,
          gunakan tab pembatalan biasa.
        </div>
        {manualInfo && (
          <div style={{ background: '#f6f8fc', border: '1px solid #e0e7f0', borderRadius: 8,
                        padding: '8px 14px', marginBottom: 14, fontSize: 12, color: '#0f2847' }}>
            Posisi aplikasi saat ini: kode <b>{manualInfo.kode || '-'}</b>, nomor terakhir
            terpakai <b>{manualInfo.nomor_terakhir}</b>. Maksimal {manualInfo.max_rentang} nomor per input.
          </div>
        )}
        <Form ref={this.formManualRef} layout="vertical" onFinish={this.handleManualPeriksa}
              initialValues={{ kode_notice: (manualInfo && manualInfo.kode) || '', kategori: 'rusak_printer' }}>
          <Row gutter={[12, 0]}>
            <Col xs={12} sm={6} md={4}>
              <Form.Item label="Kode Notice" name="kode_notice"
                         rules={[{ required: true, message: 'Wajib diisi' }]}>
                <Input style={{ borderRadius: 8, fontWeight: 700, letterSpacing: 1 }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Form.Item label="Nomor Dari" name="nomor_dari"
                         rules={[{ required: true, message: 'Wajib diisi' }]}>
                <Input type="number" min={1} className="no-uppercase"
                       style={{ borderRadius: 8, fontWeight: 700 }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Form.Item label="Sampai (opsional)" name="nomor_sampai">
                <Input type="number" min={1} className="no-uppercase"
                       placeholder="= nomor dari" style={{ borderRadius: 8, fontWeight: 700 }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={6}>
              <Form.Item label="Kategori" name="kategori"
                         rules={[{ required: true, message: 'Pilih kategori' }]}>
                <Select style={{ borderRadius: 8 }}>
                  {kategoriRusak.map(opt => (
                    <Select.Option key={opt.value} value={opt.value}>
                      <WarningOutlined style={{ color: '#fa8c16', marginRight: 6 }} />{opt.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Form.Item label="Nomor Polisi (opsional)" name="nomor_polisi">
                <Input className="no-uppercase" placeholder="cth: KH1234AB"
                       style={{ borderRadius: 8, fontWeight: 700 }} />
              </Form.Item>
            </Col>
            <Col xs={12} sm={6} md={4}>
              {/* Wajib diisi kalau Nomor Polisi diisi - dipakai backend
                  mencocokkan ke Tagihan asli (nomor_polisi + tanggal ini)
                  supaya baris ini nyambung di Laporan Penggunaan Notice. */}
              <Form.Item label="Tgl Penetapan" name="tanggal_penetapan" dependencies={['nomor_polisi']}
                         rules={[
                           ({ getFieldValue }) => ({
                             validator(_, value) {
                               if (getFieldValue('nomor_polisi') && !value) {
                                 return Promise.reject(new Error('Wajib diisi kalau nomor polisi diisi'));
                               }
                               return Promise.resolve();
                             },
                           }),
                         ]}>
                <DatePicker format="DD-MM-YYYY" style={{ borderRadius: 8, width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={6}>
              <Form.Item label="Keterangan (opsional)" name="keterangan">
                <Input className="no-uppercase" placeholder="cth: sobek di tengah tumpukan"
                       style={{ borderRadius: 8 }} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end">
            <Col>
              <Button type="primary" htmlType="submit" icon={<FileAddOutlined />}
                      loading={loadingManualPreview}
                      style={{ borderRadius: 8, fontWeight: 600, background: '#0f2847', borderColor: '#0f2847' }}>
                Periksa & Catat Rusak
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    );
  };

  renderModalManual = () => {
    const { modalManual, manualPreview, loadingManualSubmit } = this.state;
    if (!manualPreview) return null;
    const nomorList = manualPreview.nomor_rusak || [];
    return (
      <Modal title={null} centered open={modalManual} footer={null} closable={false}
             width="min(92vw, 480px)" className="modal-peringatan"
             onCancel={() => this.setState({ modalManual: false })}>
        <div className="peringatan-modal-header">
          <div className="peringatan-modal-icon"><WarningOutlined /></div>
          <div>
            <div className="peringatan-modal-title">Konfirmasi Catat Blanko Rusak</div>
            <div className="peringatan-modal-subtitle">Tanpa approval - langsung tercatat permanen</div>
          </div>
        </div>
        <div className="peringatan-modal-body">
          <div style={{ marginBottom: 10 }}>
            <b>{nomorList.length} nomor</b> berikut akan dicatat <b>RUSAK</b> dan otomatis
            dilompati saat pembayaran:
          </div>
          <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8,
                        padding: '10px 14px', marginBottom: 12, fontWeight: 700,
                        letterSpacing: 1, color: '#a8071a', maxHeight: 120,
                        overflowY: 'auto', fontSize: 14 }}>
            {manualPreview.kode} {nomorList.join(', ')}
          </div>
          {(manualPreview.nomor_dari_pool || []).length > 0 && (
            <div style={{ fontSize: 12, color: '#d46b08', marginBottom: 8 }}>
              <ExclamationCircleOutlined style={{ marginRight: 6 }} />
              Nomor {manualPreview.nomor_dari_pool.join(', ')} sedang berada di pool nomor
              kembali dan akan DIKELUARKAN dari pool.
            </div>
          )}
          {manualPreview.nomor_polisi && (
            manualPreview.tagihan_cocok ? (
              <div style={{ fontSize: 12, color: '#237804', marginBottom: 8 }}>
                <CheckCircleOutlined style={{ marginRight: 6 }} />
                Cocok dengan transaksi {manualPreview.nomor_polisi} - baris ini akan nyambung
                ke transaksi tersebut di Laporan Penggunaan Notice.
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#d46b08', marginBottom: 8 }}>
                <ExclamationCircleOutlined style={{ marginRight: 6 }} />
                Transaksi {manualPreview.nomor_polisi} tidak ditemukan (override admin) - baris
                ini akan berdiri sendiri di Laporan Penggunaan Notice, tidak nyambung ke
                transaksi manapun.
              </div>
            )
          )}
          <div style={{ fontSize: 12, color: '#888' }}>
            Nomor yang sudah tercatat rusak masuk laporan BA Notice Rusak dan tidak bisa
            dipakai ulang.
          </div>
        </div>
        <div className="peringatan-modal-footer">
          <Button onClick={() => this.setState({ modalManual: false })}
                  style={{ borderRadius: 8, marginRight: 8 }} disabled={loadingManualSubmit}>
            Batal
          </Button>
          <Button type="danger" loading={loadingManualSubmit} onClick={this.handleManualKonfirmasi}
                  style={{ borderRadius: 8, fontWeight: 600 }}>
            Ya, Catat Rusak
          </Button>
        </div>
      </Modal>
    );
  };

  // ── Status badge ─────────────────────────────────────────────
  renderStatusBadge = (s) => (
    <Tag icon={STATUS_ICON[s]} color={STATUS_COLOR[s]}
         style={{ borderRadius: 20, fontWeight: 600, padding: '2px 10px' }}>
      {STATUS_LABEL[s] || s}
    </Tag>
  );

  // ── Permintaan card ───────────────────────────────────────────
  renderPermCard = (item) => {
    const isPending = item.status === 'pending';
    const showApproveBtn = this._hasApproveInCard() && isPending;
    return (
      <Card key={item.id} size="small" style={{
        marginBottom: 12, borderRadius: 10,
        border: isPending ? '1.5px solid #faad14' : '1px solid #f0f0f0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }} bodyStyle={{ padding: '14px 16px' }}>
        <Row gutter={[8, 6]} align="middle">
          <Col flex="auto">
            <Space wrap size={6}>
              {this.renderStatusBadge(item.status)}
              <Tag color={item.jenis === 'rusak' ? 'volcano' : 'blue'} style={{ borderRadius: 20 }}>
                {item.jenis === 'rusak' ? '⚠ ' : '↩ '}{item.kategori_label || (item.jenis === 'rusak' ? 'Notice Rusak' : 'Batal Validasi')}
              </Tag>
              <Tag color={item.jenis === 'rusak' ? 'red' : 'cyan'} style={{ borderRadius: 20, fontSize: 11 }}>
                {item.jenis === 'rusak' ? 'Notice Rusak' : 'Bisa Dipakai Ulang'}
              </Tag>
              {item.sumber && item.sumber !== 'pengajuan' && (
                <Tag color="geekblue" style={{ borderRadius: 20 }}>
                  {item.sumber === 'manual' ? 'Input Manual' : 'Sinkronisasi Printer'}
                </Tag>
              )}
            </Space>
            <div style={{ marginTop: 6 }}>
              <Text strong style={{ fontSize: 15 }}>{item.nomor_polisi || 'Rusak Fisik (belum digunakan)'}</Text>
              {item.kode_notice && (
                <Tag style={{ marginLeft: 8, borderRadius: 20, fontSize: 11 }}>{item.kode_notice}</Tag>
              )}
              <Text strong style={{ fontSize: 13, marginLeft: 1, letterSpacing: 1 }}>
                {item.nomor_notice ? item.nomor_notice : '-'}
              </Text>
            </div>
            <Row gutter={[12, 4]} style={{ marginTop: 6, fontSize: 12 }}>
              <Col xs={24} sm={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Tgl Penetapan</Text>
                <div style={{ fontWeight: 600, color: '#0f2847' }}>
                  {item.created_at ? moment(item.created_at).format('DD-MM-YYYY') : '-'}
                </div>
              </Col>
              <Col xs={24} sm={12}>
                <Text type="secondary" style={{ fontSize: 11 }}>Diajukan</Text>
                <div style={{ fontWeight: 600, color: '#666' }}>
                  {moment(item.created_at).format('DD MMM YYYY HH:mm')}
                </div>
              </Col>
            </Row>
            {(this.canDirectCancel()) && item.diajukan_oleh_nama && (
              <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                Diajukan: <b>{item.diajukan_oleh_nama}</b>
                {item.upt_nama && <span style={{ color: '#aaa', marginLeft: 6 }}>({item.upt_nama})</span>}
              </div>
            )}
            {item.keterangan && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Keterangan: "{item.keterangan}"</div>}
            {item.catatan_kasi && (
              <div style={{ fontSize: 12, color: item.status === 'ditolak' ? '#cf1322' : '#389e0d', marginTop: 4 }}>
                Catatan: {item.catatan_kasi}
              </div>
            )}
          </Col>
          {showApproveBtn && (
            <Col xs={24} sm={24} md="auto" style={{ marginTop: 12 }}>
              <Space direction="vertical" size={6} style={{ width: '100%' }}>
                <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                  style={{ borderRadius: 6, background: '#52c41a', borderColor: '#52c41a', width: '100%' }}
                  onClick={() => this.setState({ selectedPerm: item, modalApprove: true, catatanKasi: '' })}>
                  Setujui</Button>
                <Button size="small" danger icon={<CloseCircleOutlined />}
                  style={{ borderRadius: 6, width: '100%' }}
                  onClick={() => this.setState({ selectedPerm: item, modalReject: true, catatanKasi: '' })}>
                  Tolak</Button>
              </Space>
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  _hasApproveInCard = () => this.isApprover() || this.isSuperadmin() || this.isAdmin();

  // ── Header config per level ───────────────────────────────────
  _headerCfg = () => {
    if (this.isSuperadmin()) return {
      bg: 'linear-gradient(135deg, #1d3557 0%, #457b9d 100%)',
      icon: <CrownOutlined />, title: 'Batal Notice — Superadmin',
      sub: 'Semua UPT — batalkan langsung & lihat log lengkap',
    };
    if (this.isAdmin()) return {
      bg: 'linear-gradient(135deg, #2d6a4f 0%, #40916c 100%)',
      icon: <TeamOutlined />, title: 'Batal Notice — Admin',
      sub: 'UPT login — batalkan langsung & lihat log',
    };
    if (this.isApprover()) return {
      bg: 'linear-gradient(135deg, #1a6b3c 0%, #2d8a50 100%)',
      icon: <SafetyCertificateOutlined />, title: 'Persetujuan Batal Notice',
      sub: 'Tinjau permintaan dari kasir atau batalkan langsung',
    };
    return {
      bg: 'linear-gradient(135deg, #0f2847 0%, #1e4d8a 100%)',
      icon: <UserOutlined />, title: 'Batal Notice',
      sub: 'Ajukan permintaan pembatalan notice',
    };
  };

  // ── Tagihan table (shared) ────────────────────────────────────
  renderTagihanSection = (isDirectMode) => {
    const { loadingTagihan, listTagihan, searchNopol, listPermintaan, pagination, isMobile } = this.state;
    const showUptCol = this.canSeeAll();

    const actionCell = (row) => {
      const hasPending = listPermintaan.some(p => Number(p.tagihan) === Number(row.id) && p.status === 'pending');
      const isCancelled = row.is_notice_failed && row.status !== 3;
      if (isDirectMode) {
        if (isCancelled) return <Tag color="orange">Dibatalkan</Tag>;
        if (hasPending) return null;
        return (
          <Button size="small" danger icon={<ScissorOutlined />} style={{ borderRadius: 6 }}
            onClick={() => this.setState({ selectedTagihan: row, modalBatalkan: true })}>
            Batalkan</Button>
        );
      }
      if (isCancelled) return <Tag color="orange">Dibatalkan</Tag>;
      if (hasPending) return (
        <Button size="small" disabled icon={<ClockCircleOutlined />}
          style={{ borderRadius: 6, color: '#d46b08', borderColor: '#ffd666', background: '#fffbe6', cursor: 'default' }}>
          Menunggu</Button>
      );
      return (
        <Button size="small" type="primary" icon={<SendOutlined />}
          style={{ borderRadius: 6, background: '#0f2847', borderColor: '#0f2847' }}
          onClick={() => this.setState({ selectedTagihan: row, modalAjukan: true })}>
          Ajukan</Button>
      );
    };

    const cols = [
      { title: 'Tgl Penetapan', dataIndex: 'tgl_penetapan', width: 130, align: 'center',
        render: v => <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {v ? moment(v, 'YYYY-MM-DD').format('DD-MM-YYYY') : '-'}</span> },
      { title: 'Nomor Polisi', dataIndex: 'nomor_polisi', width: 120, align: 'center',
        render: v => <Text strong style={{ color: '#0f2847', fontSize: 12 }}>{v}</Text> },
      { title: 'Nama WP', dataIndex: 'nama_wp', width: 140, align: 'center', ellipsis: true,
        render: v => <span style={{ fontSize: 12 }}>{v || '-'}</span> },
      { title: 'Layanan', dataIndex: 'layanan_list', width: 140, align: 'center', ellipsis: true,
        render: v => <span style={{ fontSize: 12 }}>{Array.isArray(v) ? v.map(l => l.nama).join(', ') : '-'}</span> },
      { title: 'Kode Notice', dataIndex: 'notice_number', width: 120, align: 'center',
        render: (v, row) => <Text strong style={{ letterSpacing: 1, fontSize: 12 }}>{fmtNotice(row.kode_notice, v)}</Text> },
      ...(showUptCol ? [{ title: 'UPT', dataIndex: 'kabupaten_nama', width: 130,
        render: v => <Tag color="blue" style={{ fontSize: 11 }}>{v || '-'}</Tag> }] : []),
      { title: 'Status', dataIndex: 'is_notice_failed', width: 140, align: 'center',
        render: (v, row) => {
          const hasPending = listPermintaan.some(p => Number(p.tagihan) === Number(row.id) && p.status === 'pending');
          if (v && row.status !== 3) return <Tag color="orange" style={{ fontSize: 11 }}>Dibatalkan</Tag>;
          if (hasPending) return <Tag color="gold" icon={<ClockCircleOutlined style={{ fontSize: 10 }} />} style={{ fontSize: 11 }}>Pending</Tag>;
          return <Tag color="green" style={{ fontSize: 11 }}>Aktif</Tag>;
        }
      },
      { title: 'Aksi', width: 130, fixed: 'right', align: 'center', render: (_, row) => actionCell(row) },
    ];

    return (
      <div>
        <Row gutter={[8, 8]} style={{ marginBottom: 12 }} align="middle">
          <Col xs={24} sm={16} md={14}>
            <Input.Search placeholder="Cari nomor polisi..." allowClear
              enterButton={<SearchOutlined />} value={searchNopol}
              onChange={e => this.setState({ searchNopol: e.target.value })}
              onSearch={v => this.fetchTagihan(v)}
              style={{ borderRadius: 8 }} />
          </Col>
          <Col xs={24} sm={8} md="auto">
            <Button icon={<ReloadOutlined />} block={window.innerWidth <= 768}
              onClick={() => this.fetchTagihan(searchNopol)}
              style={{ borderRadius: 8 }}>Refresh</Button>
          </Col>
        </Row>
        {isMobile ? (
          <div>
            {loadingTagihan ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
              : listTagihan.length === 0 ? <Empty description="Tidak ada data" style={{ padding: 40 }} />
              : listTagihan.map(row => {
                  const hasPending = listPermintaan.some(p => Number(p.tagihan) === Number(row.id) && p.status === 'pending');
                  const isCancelled = row.is_notice_failed && row.status !== 3;
                  return (
                    <Card key={row.id} size="small" style={{ marginBottom: 10, borderRadius: 10, borderLeft: '4px solid #1e4d8a' }}
                          bodyStyle={{ padding: '12px 14px' }}>
                      <Row gutter={[8, 8]}>
                        <Col xs={24}>
                          <Text strong style={{ fontSize: 15, color: '#0f2847' }}>{row.nomor_polisi}</Text>
                          <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{row.nama_wp}</div>
                          <div style={{ fontSize: 11, color: '#555', fontWeight: 600, letterSpacing: 1, marginTop: 2 }}>
                            {fmtNotice(row.kode_notice, row.notice_number)}
                          </div>
                        </Col>
                        <Col xs={12}>
                          <div style={{ fontSize: 11, color: '#999' }}>Tgl Penetapan</div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#0f2847' }}>
                            {row.created_at ? moment(row.created_at).format('DD MMM YY') : '-'}
                          </div>
                        </Col>
                        <Col xs={12}>
                          <div style={{ fontSize: 11, color: '#999' }}>Status</div>
                          {isCancelled ? <Tag color="orange" style={{ fontSize: 11 }}>Dibatalkan</Tag>
                            : hasPending ? <Tag color="gold" icon={<ClockCircleOutlined />} style={{ fontSize: 11 }}>Pending</Tag>
                            : <Tag color="green" style={{ fontSize: 11 }}>Aktif</Tag>}
                        </Col>
                        {showUptCol && <Col xs={24}>
                          <div style={{ fontSize: 11, color: '#999' }}>UPT</div>
                          <Tag color="blue" style={{ fontSize: 11 }}>{row.kabupaten_nama || '-'}</Tag>
                        </Col>}
                        <Col xs={24} style={{ marginTop: 8 }}>
                          {actionCell(row)}
                        </Col>
                      </Row>
                    </Card>
                  );
              })}
          </div>
        ) : (
          <Table rowKey="id" columns={cols} dataSource={listTagihan} loading={loadingTagihan}
            scroll={{ x: showUptCol ? 1150 : 950 }}
            pagination={{ ...pagination, showSizeChanger: false,
              showTotal: t => `Total ${t} data`,
              onChange: p => this.fetchTagihan(searchNopol, p) }}
            size="small" />
        )}
      </div>
    );
  };

  // ── Log view (superadmin/admin) ───────────────────────────────
  renderLogView = () => {
    const { loadingLog, listLog, logFilter, logPagination, isMobile, listUpt, loadingUpt } = this.state;
    const cols = [
      { title: 'Tanggal', dataIndex: 'created_at', width: 130, align: 'center',
        render: v => <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{moment(v).format('DD-MM-YYYY HH:mm')}</span> },
      { title: 'Tgl Penetapan', dataIndex: 'created_at', width: 110, align: 'center',
        render: v => <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          {v ? moment(v).format('DD-MM-YYYY') : '-'}</span> },
      { title: 'Status', dataIndex: 'status', width: 100, align: 'center', render: v => this.renderStatusBadge(v) },
      { title: 'Kategori', dataIndex: 'jenis', width: 170, align: 'center',
        render: (v, row) => (
          <span>
            <Tag color={v === 'rusak' ? 'volcano' : 'blue'} style={{ borderRadius: 20, fontSize: 11 }}>
              {v === 'rusak' ? '⚠ ' : '↩ '}{row.kategori_label || (v === 'rusak' ? 'Notice Rusak' : 'Batal Validasi')}</Tag>
            {row.sumber && row.sumber !== 'pengajuan' && (
              <Tag color="geekblue" style={{ borderRadius: 20, fontSize: 10, marginTop: 4 }}>
                {row.sumber === 'manual' ? 'Input Manual' : 'Sinkronisasi'}
              </Tag>
            )}
          </span>
        ) },
      { title: 'Nopol / Kode Notice', dataIndex: 'nomor_polisi', width: 150,
        render: (v, row) => (
          <div>
            <Text strong style={{ color: '#0f2847', fontSize: 12 }}>{v || 'Rusak Fisik'}</Text>
            {row.kode_notice && (
              <Tag style={{ marginLeft: 6, borderRadius: 20, fontSize: 10 }}>{row.kode_notice}</Tag>
            )}
            <div style={{ fontSize: 11, color: '#999' }}>Notice: {row.nomor_notice}</div>
          </div>
        )
      },
      { title: 'Diajukan Oleh', dataIndex: 'diajukan_oleh_nama', width: 140, align: 'center',
        render: (v, row) => <div style={{ fontSize: 12 }}>
          <div style={{ fontWeight: 600 }}>{v || '-'}</div>
          <div style={{ fontSize: 11, color: '#999' }}>{row.upt_nama || '-'}</div>
        </div> },
      { title: 'Disetujui/Ditolak', dataIndex: 'disetujui_oleh_nama', width: 140, align: 'center',
        render: v => <span style={{ fontSize: 12 }}>{v || '-'}</span> },
      { title: 'Catatan', ellipsis: true, align: 'center',
        render: (_, row) => <span style={{ fontSize: 12 }}>
          {row.keterangan ? `Ket: ${row.keterangan}` : ''}{row.catatan_kasi ? ` | ${row.catatan_kasi}` : ''}</span> },
    ];

    return (
      <div>
        {/* Filter bar */}
        <Row gutter={[8, 8]} style={{ marginBottom: 12 }} align="middle">
          <Col xs={24} sm={24} md={10}>
            <RangePicker size="small" style={{ width: '100%', borderRadius: 8 }}
              placeholder={['Tanggal mulai', 'Tanggal akhir']}
              onChange={(_, strings) => this.setState({
                logFilter: { ...logFilter, tanggal_dari: strings[0], tanggal_sampai: strings[1] }
              })} />
          </Col>
          {this.canSeeAll() && (
            <Col xs={24} sm={24} md={8}>
              <Select size="small" allowClear placeholder="Filter UPT" loading={loadingUpt}
                style={{ width: '100%', borderRadius: 8 }}
                onChange={v => this.setState({ logFilter: { ...logFilter, upt_id: v || '' } })}>
                {listUpt.map(u => <Select.Option key={u.id} value={u.id}>{u.nama}</Select.Option>)}
              </Select>
            </Col>
          )}
          <Col xs={12} sm={12} md="auto">
            <Button type="primary" size="small" icon={<SearchOutlined />}
              block style={{ borderRadius: 8 }} onClick={() => this.fetchLog(1)}>
              Cari</Button>
          </Col>
          <Col xs={12} sm={12} md="auto">
            <Button size="small" icon={<ReloadOutlined />}
              block style={{ borderRadius: 8 }} onClick={() => this.fetchLog(1)}>
              Refresh</Button>
          </Col>
        </Row>

        {isMobile ? (
          <div>
            {loadingLog ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
              : listLog.length === 0 ? <Empty description="Belum ada log" style={{ padding: 40 }} />
              : listLog.map(item => (
                  <Card key={item.id} size="small" style={{ marginBottom: 10, borderRadius: 10, borderLeft: '4px solid #1e4d8a' }}
                        bodyStyle={{ padding: '12px 14px' }}>
                    <Space wrap size={6} style={{ marginBottom: 8 }}>
                      {this.renderStatusBadge(item.status)}
                      <Tag color={item.jenis === 'rusak' ? 'volcano' : 'blue'} style={{ borderRadius: 20, fontSize: 11 }}>
                        {item.jenis === 'rusak' ? '⚠ ' : '↩ '}{item.kategori_label || (item.jenis === 'rusak' ? 'Notice Rusak' : 'Batal Validasi')}
                      </Tag>
                      {item.sumber && item.sumber !== 'pengajuan' && (
                        <Tag color="geekblue" style={{ borderRadius: 20, fontSize: 10 }}>
                          {item.sumber === 'manual' ? 'Input Manual' : 'Sinkronisasi'}
                        </Tag>
                      )}
                    </Space>
                    <div style={{ marginBottom: 8 }}>
                      <Text strong style={{ color: '#0f2847', fontSize: 13 }}>{item.nomor_polisi || 'Rusak Fisik'}</Text>
                      {item.kode_notice && (
                        <Tag style={{ marginLeft: 6, borderRadius: 20, fontSize: 10 }}>{item.kode_notice}</Tag>
                      )}
                      <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Notice: {item.nomor_notice}</div>
                    </div>
                    <Row gutter={[8, 6]} style={{ marginBottom: 8, fontSize: 12 }}>
                      <Col xs={12}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Tgl Penetapan</Text>
                        <div style={{ fontWeight: 600, color: '#0f2847' }}>
                          {item.created_at ? moment(item.created_at).format('DD MMM YY') : '-'}
                        </div>
                      </Col>
                      <Col xs={12}>
                        <Text type="secondary" style={{ fontSize: 11 }}>Diajukan</Text>
                        <div style={{ fontWeight: 600, color: '#666' }}>
                          {moment(item.created_at).format('DD MMM HH:mm')}
                        </div>
                      </Col>
                    </Row>
                    <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
                      <b>{item.diajukan_oleh_nama || '-'}</b>
                      {item.upt_nama && <span style={{ color: '#999', marginLeft: 6 }}>({item.upt_nama})</span>}
                    </div>
                    {item.disetujui_oleh_nama && (
                      <div style={{ fontSize: 12, color: '#389e0d', marginBottom: 4 }}>
                        Disetujui: <b>{item.disetujui_oleh_nama}</b>
                      </div>
                    )}
                    {item.catatan_kasi && <div style={{ fontSize: 12, color: '#888', marginTop: 4, fontStyle: 'italic' }}>"{item.catatan_kasi}"</div>}
                  </Card>
              ))}
          </div>
        ) : (
          <Table rowKey="id" columns={cols} dataSource={listLog} loading={loadingLog}
            scroll={{ x: 1150 }} size="small"
            pagination={{ ...logPagination, showSizeChanger: false,
              showTotal: t => `Total ${t} data`, onChange: p => this.fetchLog(p) }} />
        )}
      </div>
    );
  };

  render() {
    const {
      loadingPermintaan, listPermintaan, tabAktif,
      modalAjukan, loadingAjukan, selectedTagihan,
      modalBatalkan, loadingBatalkan,
      modalApprove, modalReject, selectedPerm, loadingAction, catatanKasi,
      isMobile, permintaanFilterStatus,
    } = this.state;

    const pendingList = listPermintaan.filter(p => p.status === 'pending');
    const pendingCount = pendingList.length;
    const permintaanFiltered = listPermintaan.filter(p => p.status === permintaanFilterStatus);
    const PERMINTAAN_STATUS_LABEL = { pending: 'permintaan pending', disetujui: 'yang disetujui', ditolak: 'yang ditolak' };
    const hCfg = this._headerCfg();

    // ── Tab items berdasarkan level ────────────────────────────
    let tabItems = [];

    if (this.isSuperadmin() || this.isAdmin()) {
      tabItems = [
        {
          key: 'batalkan',
          label: <span><ScissorOutlined style={{ color: '#cf1322' }} /> Batalkan Langsung</span>,
          children: (
            <div>
              <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8,
                             padding: '8px 14px', marginBottom: 14, fontSize: 12, color: '#a8071a' }}>
                <AuditOutlined style={{ marginRight: 8 }} />
                {this.isSuperadmin()
                  ? 'Superadmin — melihat semua UPT. Setiap pembatalan tersimpan otomatis untuk audit.'
                  : 'Admin — melihat UPT login. Setiap pembatalan tersimpan otomatis untuk audit.'}
                {' '}Nopol dengan tag <Tag color="gold" size="small">Ada Pengajuan Pending</Tag> tidak bisa dibatalkan langsung (proses via tab Permintaan Masuk).
              </div>
              {this.renderTagihanSection(true)}
            </div>
          ),
        },
        {
          key: 'permintaan',
          label: (
            <span>
              <Badge count={pendingCount} size="small" style={{ marginRight: 6 }}><ClockCircleOutlined /></Badge>
              {' '}Permintaan Masuk
            </span>
          ),
          children: (
            <div>
              {/* Stats bar — klik utk filter list di bawah */}
              <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
                {[
                  { key: 'pending',   label: 'Menunggu',  count: listPermintaan.filter(p => p.status === 'pending').length,   color: '#faad14', bg: '#fffbe6', border: '#ffe58f', icon: <ClockCircleOutlined /> },
                  { key: 'disetujui', label: 'Disetujui', count: listPermintaan.filter(p => p.status === 'disetujui').length, color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', icon: <CheckCircleOutlined /> },
                  { key: 'ditolak',   label: 'Ditolak',   count: listPermintaan.filter(p => p.status === 'ditolak').length,   color: '#ff4d4f', bg: '#fff2f0', border: '#ffccc7', icon: <CloseCircleOutlined /> },
                ].map(s => (
                  <Col xs={8} key={s.key}>
                    <div
                      onClick={() => this.setState({ permintaanFilterStatus: s.key })}
                      style={{
                        background: s.bg, border: `1.5px solid ${s.border}`,
                        borderRadius: 10, padding: '10px 14px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8,
                        boxShadow: permintaanFilterStatus === s.key ? `0 0 0 2px ${s.color}66` : 'none',
                      }}
                    >
                      <span style={{ fontSize: 18, color: s.color }}>{s.icon}</span>
                      <div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: s.color, lineHeight: 1.1 }}>{s.count}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>{s.label}</div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
              {loadingPermintaan ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                : permintaanFiltered.length === 0 ? <Empty description={`Tidak ada ${PERMINTAAN_STATUS_LABEL[permintaanFilterStatus]}`} style={{ padding: 40 }} />
                : permintaanFiltered.map(item => this.renderPermCard(item))}
            </div>
          ),
        },
        {
          key: 'log',
          label: <span><AuditOutlined /> Log Pembatalan</span>,
          children: this.renderLogView(),
        },
      ];
    } else if (this.isApprover()) {
      tabItems = [
        {
          key: 'batalkan',
          label: <span><ScissorOutlined style={{ color: '#cf1322' }} /> Batalkan Langsung</span>,
          children: (
            <div>
              <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8,
                             padding: '8px 14px', marginBottom: 14, fontSize: 12, color: '#a8071a' }}>
                <AuditOutlined style={{ marginRight: 8 }} />
                Nopol dengan tag <Tag color="gold" size="small">Ada Pengajuan Pending</Tag> tidak bisa dibatalkan langsung (proses via tab Permintaan Masuk).
              </div>
              {this.renderTagihanSection(true)}
            </div>
          ),
        },
        {
          key: 'permintaan',
          label: (
            <span>
              <Badge count={pendingCount} size="small" style={{ marginRight: 6 }}><ClockCircleOutlined /></Badge>
              {' '}Permintaan Masuk
            </span>
          ),
          children: (
            <div>
              {/* Stats bar — klik utk filter list di bawah */}
              <Row gutter={[10, 10]} style={{ marginBottom: 16 }}>
                {[
                  { key: 'pending',   label: 'Menunggu',  count: listPermintaan.filter(p => p.status === 'pending').length,   color: '#faad14', bg: '#fffbe6', border: '#ffe58f', icon: <ClockCircleOutlined /> },
                  { key: 'disetujui', label: 'Disetujui', count: listPermintaan.filter(p => p.status === 'disetujui').length, color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', icon: <CheckCircleOutlined /> },
                  { key: 'ditolak',   label: 'Ditolak',   count: listPermintaan.filter(p => p.status === 'ditolak').length,   color: '#ff4d4f', bg: '#fff2f0', border: '#ffccc7', icon: <CloseCircleOutlined /> },
                ].map(s => (
                  <Col xs={8} key={s.key}>
                    <div
                      onClick={() => this.setState({ permintaanFilterStatus: s.key })}
                      style={{
                        background: s.bg, border: `1.5px solid ${s.border}`,
                        borderRadius: 10, padding: '10px 14px', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 8,
                        boxShadow: permintaanFilterStatus === s.key ? `0 0 0 2px ${s.color}66` : 'none',
                      }}
                    >
                      <span style={{ fontSize: 18, color: s.color }}>{s.icon}</span>
                      <div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: s.color, lineHeight: 1.1 }}>{s.count}</div>
                        <div style={{ fontSize: 11, color: '#888' }}>{s.label}</div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
              {loadingPermintaan ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                : permintaanFiltered.length === 0 ? <Empty description={`Tidak ada ${PERMINTAAN_STATUS_LABEL[permintaanFilterStatus]}`} style={{ padding: 40 }} />
                : permintaanFiltered.map(item => this.renderPermCard(item))}
            </div>
          ),
        },
        {
          key: 'riwayat',
          label: <span><FileTextOutlined /> Riwayat</span>,
          children: (
            <div>
              {loadingPermintaan ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                : listPermintaan.filter(p => p.status !== 'pending').length === 0
                  ? <Empty description="Belum ada riwayat" style={{ padding: 40 }} />
                  : listPermintaan.filter(p => p.status !== 'pending').map(item => this.renderPermCard(item))}
            </div>
          ),
        },
      ];
    } else {
      // Kasir
      tabItems = [
        {
          key: 'ajukan',
          label: <span><SendOutlined /> Ajukan Pembatalan</span>,
          children: this.renderTagihanSection(false),
        },
        {
          key: 'riwayat',
          label: (
            <span>
              <FileTextOutlined /> Riwayat Pengajuan
              {pendingCount > 0 && <Badge count={pendingCount} size="small" style={{ marginLeft: 6 }} />}
            </span>
          ),
          children: (
            <div>
              {loadingPermintaan ? <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
                : listPermintaan.length === 0 ? <Empty description="Belum ada pengajuan" style={{ padding: 40 }} />
                : listPermintaan.map(item => this.renderPermCard(item))}
            </div>
          ),
        },
      ];
    }

    // Tab Input Manual - untuk semua role yang punya akses (level 1/2 atau
    // key transaksi_batal_notice_manual), ditempel setelah tab bawaan role.
    if (this.canInputManual()) {
      tabItems.push({
        key: 'input_manual',
        label: <span><FileAddOutlined style={{ color: '#d46b08' }} /> Input Manual</span>,
        children: this.renderManualTab(),
      });
    }

    return (
      <div>
        <Helmet><title>{configClass.title + ' | ' + config.get_site_info().name}</title></Helmet>


        {/* Header */}
        <Card bodyStyle={{ padding: 0 }} style={{
          borderRadius: 14, overflow: 'hidden', marginBottom: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.10)', border: 'none',
        }}>
          <div style={{ background: hCfg.bg, padding: '20px 24px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexWrap: 'wrap', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{ width: 50, height: 50, borderRadius: 12, background: 'rgba(255,255,255,0.15)',
                             display: 'flex', alignItems: 'center', justifyContent: 'center',
                             fontSize: 22, color: '#fff' }}>
                {hCfg.icon}
              </div>
              <div>
                <div style={{ color: '#fff', fontWeight: 700, fontSize: 17 }}>{hCfg.title}</div>
                <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>{hCfg.sub}</div>
              </div>
            </div>
            {this.canDirectCancel() && pendingCount > 0 && (
              <Tag style={{ borderRadius: 20, padding: '4px 14px',
                             background: 'rgba(255,193,7,0.25)', border: '1px solid rgba(255,193,7,0.6)',
                             color: '#ffd700', fontWeight: 600, fontSize: 13 }}>
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                {pendingCount} Permintaan Pending
              </Tag>
            )}
          </div>
        </Card>

        {/* Main */}
        <Card style={{ borderRadius: 14, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: 'none' }}
              bodyStyle={{ padding: isMobile ? '12px' : '20px 24px' }}>
          <Tabs activeKey={tabAktif || tabItems[0]?.key}
            onChange={key => {
              this.setState({ tabAktif: key });
              if (key === 'permintaan' || key === 'riwayat') this.fetchPermintaan();
              if (key === 'log') this.fetchLog();
            }}
            items={tabItems} type="card" />
        </Card>

        {/* Modal Ajukan (Kasir) */}
        <Modal title={<span><SendOutlined style={{ color: '#0f2847', marginRight: 8 }} />Ajukan Pembatalan Notice</span>}
          open={modalAjukan} onCancel={() => this.setState({ modalAjukan: false })}
          footer={null} destroyOnClose width={isMobile ? '95%' : 480}>
          {selectedTagihan && (
            <div>
              <div style={{ background: '#f6f8fc', borderRadius: 8, padding: 12, marginBottom: 16,
                             border: '1px solid #e0e7f0' }}>
                <Row gutter={8}>
                  <Col span={12}><Text type="secondary" style={{ fontSize: 11 }}>Nomor Polisi</Text>
                    <div><Text strong style={{ fontSize: 15, color: '#cf1322' }}>{selectedTagihan.nomor_polisi}</Text></div></Col>
                  <Col span={12}><Text type="secondary" style={{ fontSize: 11 }}>Kode Notice</Text>
                    <div><Text strong style={{ fontSize: 15, letterSpacing: 1 }}>
                      {fmtNotice(selectedTagihan.kode_notice, selectedTagihan.notice_number)}
                    </Text></div></Col>
                  <Col span={24} style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 11 }}>UPT</Text>
                    <div><Text style={{ fontSize: 13 }}>{selectedTagihan.kabupaten_nama || '-'}</Text></div></Col>
                  <Col span={24} style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 11 }}>Tgl Penetapan</Text>
                    <div><Text strong style={{ fontSize: 13 }}>{selectedTagihan.tgl_penetapan ? moment(selectedTagihan.tgl_penetapan).format('DD-MM-YYYY') : '-'}</Text></div></Col>
                </Row>
              </div>
              <Form ref={this.formRef} layout="vertical" onFinish={this.confirmAjukan}>
                <Form.Item label="Kategori Pembatalan" name="kategori" rules={[{ required: true, message: 'Pilih kategori' }]}>
                  <Select placeholder="Pilih kategori pembatalan" style={{ borderRadius: 8 }}>
                    {KATEGORI_OPTIONS.map(opt => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.jenis === 'rusak'
                          ? <span><WarningOutlined style={{ color: '#fa8c16', marginRight: 6 }} />{opt.label} <Text type="secondary" style={{ fontSize: 11 }}>— Notice Rusak</Text></span>
                          : <span><CloseCircleOutlined style={{ color: '#1890ff', marginRight: 6 }} />{opt.label} <Text type="secondary" style={{ fontSize: 11 }}>— Notice Tidak Rusak</Text></span>}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                  *Kategori "Batal Pembayaran" — nomor notice bisa dipakai ulang. Kategori lainnya — nomor TIDAK bisa dipakai ulang.
                </div>
                <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8,
                               padding: '8px 12px', marginBottom: 16 }}>
                  <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                  <Text style={{ fontSize: 12, color: '#614700' }}>Permintaan akan dikirim ke Kasi Penetapan untuk disetujui.</Text>
                </div>
                <Row gutter={8} justify="end">
                  <Col><Button onClick={() => this.setState({ modalAjukan: false })} style={{ borderRadius: 8 }}>Batal</Button></Col>
                  <Col><Button type="primary" htmlType="submit" loading={loadingAjukan} icon={<SendOutlined />}
                    style={{ borderRadius: 8, background: '#0f2847', borderColor: '#0f2847' }}>Kirim</Button></Col>
                </Row>
              </Form>
            </div>
          )}
        </Modal>

        {/* Modal Batalkan Langsung */}
        <Modal title={<span><ScissorOutlined style={{ color: '#cf1322', marginRight: 8 }} />Batalkan Notice Langsung</span>}
          open={modalBatalkan} onCancel={() => this.setState({ modalBatalkan: false })}
          footer={null} destroyOnClose width={isMobile ? '95%' : 490}>
          {selectedTagihan && (
            <div>
              <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                <Row gutter={8}>
                  <Col span={12}><Text type="secondary" style={{ fontSize: 11 }}>Nomor Polisi</Text>
                    <div><Text strong style={{ fontSize: 15, color: '#cf1322' }}>{selectedTagihan.nomor_polisi}</Text></div></Col>
                  <Col span={12}><Text type="secondary" style={{ fontSize: 11 }}>Kode Notice</Text>
                    <div><Text strong style={{ fontSize: 15, letterSpacing: 1 }}>
                      {fmtNotice(selectedTagihan.kode_notice, selectedTagihan.notice_number)}
                    </Text></div></Col>
                  <Col span={24} style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 11 }}>UPT</Text>
                    <div><Text style={{ fontSize: 13 }}>{selectedTagihan.kabupaten_nama || '-'}</Text></div></Col>
                  <Col span={24} style={{ marginTop: 4 }}><Text type="secondary" style={{ fontSize: 11 }}>Tgl Penetapan</Text>
                    <div><Text strong style={{ fontSize: 13 }}>{selectedTagihan.tgl_penetapan ? moment(selectedTagihan.tgl_penetapan).format('DD-MM-YYYY') : '-'}</Text></div></Col>
                </Row>
              </div>
              <Form ref={this.formBatalRef} layout="vertical" onFinish={this.confirmBatalkanLangsung}>
                <Form.Item label="Kategori Pembatalan" name="kategori" rules={[{ required: true, message: 'Pilih kategori' }]}>
                  <Select
                    placeholder="Pilih kategori pembatalan"
                    disabled={selectedTagihan.paid_on
                      ? !moment(selectedTagihan.paid_on).isSame(moment(), 'day')
                      : true}
                    style={{ borderRadius: 8 }}
                  >
                    {KATEGORI_OPTIONS.map(opt => (
                      <Select.Option key={opt.value} value={opt.value}>
                        {opt.jenis === 'rusak'
                          ? <span><WarningOutlined style={{ color: '#fa8c16', marginRight: 6 }} />{opt.label} <Text type="secondary" style={{ fontSize: 11 }}>— Notice Rusak</Text></span>
                          : <span><CloseCircleOutlined style={{ color: '#1890ff', marginRight: 6 }} />{opt.label} <Text type="secondary" style={{ fontSize: 11 }}>— Notice Tidak Rusak</Text></span>}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
                  *Kategori "Batal Pembayaran" — nomor notice bisa dipakai ulang. Kategori lainnya — nomor TIDAK bisa dipakai ulang.
                </div>
                {(() => {
                  const isSameDay = selectedTagihan.paid_on
                    ? moment(selectedTagihan.paid_on).isSame(moment(), 'day')
                    : false;
                  return (
                    <>
                      {!isSameDay && (
                        <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8,
                                       padding: '8px 12px', marginBottom: 16 }}>
                          <WarningOutlined style={{ color: '#cf1322', marginRight: 8 }} />
                          <Text style={{ fontSize: 12, color: '#cf1322' }}>Pembatalan hanya dapat dilakukan pada Hari yang Sama dengan tanggal penetapan ({selectedTagihan.paid_on ? moment(selectedTagihan.paid_on).format('DD-MM-YYYY') : '-'}).</Text>
                        </div>
                      )}
                      <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8,
                                     padding: '8px 12px', marginBottom: 16 }}>
                        <AuditOutlined style={{ color: '#389e0d', marginRight: 8 }} />
                        <Text style={{ fontSize: 12, color: '#135200' }}>Log tersimpan otomatis untuk audit.</Text>
                      </div>
                    </>
                  );
                })()}
                <Row gutter={8} justify="end">
                  <Col><Button onClick={() => this.setState({ modalBatalkan: false })} style={{ borderRadius: 8 }}>Batal</Button></Col>
                  <Col><Button danger htmlType="submit" loading={loadingBatalkan} icon={<ScissorOutlined />}
                    disabled={selectedTagihan.paid_on
                      ? !moment(selectedTagihan.paid_on).isSame(moment(), 'day')
                      : true}
                    style={{ borderRadius: 8 }}>
                    Batalkan Sekarang</Button></Col>
                </Row>
              </Form>
            </div>
          )}
        </Modal>

        {/* Modal Approve */}
        <Modal title={<span><CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />Setujui Pembatalan</span>}
          open={modalApprove} onCancel={() => this.setState({ modalApprove: false })}
          onOk={this.handleApprove} okText="Setujui & Batalkan"
          okButtonProps={{ style: { background: '#52c41a', borderColor: '#52c41a', borderRadius: 8 }, loading: loadingAction }}
          cancelButtonProps={{ style: { borderRadius: 8 } }} destroyOnClose width={isMobile ? '95%' : 460}>
          {selectedPerm && (
            <div>
              <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                <div>
                  <Text strong style={{ fontSize: 15 }}>{selectedPerm.nomor_polisi}</Text>
                  {selectedPerm.kode_notice && (
                    <Tag style={{ marginLeft: 8, borderRadius: 20 }}>{selectedPerm.kode_notice}</Tag>
                  )}
                  <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>Notice: {selectedPerm.nomor_notice}</Text>
                </div>
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  Diajukan: <b>{selectedPerm.diajukan_oleh_nama}</b>
                  <span style={{ color: '#999', marginLeft: 6 }}>{selectedPerm.upt_nama}</span>
                </div>
                {selectedPerm.keterangan && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>"{selectedPerm.keterangan}"</div>}
              </div>
              <Form.Item label="Catatan (opsional)">
                <TextArea rows={2} value={catatanKasi} onChange={e => this.setState({ catatanKasi: e.target.value })}
                  placeholder="Catatan untuk kasir..." style={{ borderRadius: 8 }} className="no-uppercase" />
              </Form.Item>
              <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 8, padding: '8px 12px' }}>
                <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
                <Text style={{ fontSize: 12, color: '#614700' }}>Notice akan langsung dibatalkan setelah disetujui.</Text>
              </div>
            </div>
          )}
        </Modal>

        {/* Modal konfirmasi input manual */}
        {this.renderModalManual()}

        {/* Modal Reject */}
        <Modal title={<span><CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />Tolak Permintaan</span>}
          open={modalReject} onCancel={() => this.setState({ modalReject: false })}
          onOk={this.handleReject} okText="Tolak"
          okButtonProps={{ danger: true, style: { borderRadius: 8 }, loading: loadingAction }}
          cancelButtonProps={{ style: { borderRadius: 8 } }} destroyOnClose width={isMobile ? '95%' : 460}>
          {selectedPerm && (
            <div>
              <div style={{ background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 8, padding: 12, marginBottom: 14 }}>
                <Text strong style={{ fontSize: 15 }}>{selectedPerm.nomor_polisi}</Text>
                <span style={{ color: '#888', marginLeft: 10 }}>— {selectedPerm.diajukan_oleh_nama}</span>
              </div>
              <Form.Item label={<span>Alasan Penolakan <Text type="danger">*</Text></span>}>
                <TextArea rows={3} value={catatanKasi} onChange={e => this.setState({ catatanKasi: e.target.value })}
                  placeholder="Tulis alasan yang jelas..." style={{ borderRadius: 8 }} className="no-uppercase" />
              </Form.Item>
            </div>
          )}
        </Modal>
      </div>
    );
  }
}

const mapStateToProps = state => state.configReducers;
export default connect(mapStateToProps, { initHandler, ajaxViewHandler, ajaxHandler, setConfig })(CancelNotice);
